import { CellComponent, Formatter, Validator } from 'tabulator-tables';
import { MassiaTabulatorColumnDef, MassiaTabulatorFeatures } from '../massia.tabulator.type';

export class MassiaTabulatorColumnsBuilder {
	/**
	 * Prépare et enrichit les définitions de colonnes avant de les passer à Tabulator.
	 *
	 * @param columns Colonnes brutes de la configuration
	 * @param features Fonctionnalités activées (pour la sélection, le filtrage, etc.)
	 * @param searchQuery Requête de recherche textuelle globale (pour le surlignage)
	 * @returns Les colonnes prêtes à l'emploi
	 */
	static build(columns: MassiaTabulatorColumnDef[], features: MassiaTabulatorFeatures, searchQuery: string): MassiaTabulatorColumnDef[] {
		const processedColumns = [...(columns || [])];

		// Add selection column
		if (features.selection) {
			const hasSelectionCol = processedColumns.some((c) => c.formatter === 'rowSelection');
			if (!hasSelectionCol) {
				processedColumns.unshift({
					formatter: 'rowSelection',
					titleFormatter: 'rowSelection',
					headerSort: false,
					width: 40,
					hozAlign: 'center',
					headerHozAlign: 'center',
					frozen: true
				} as MassiaTabulatorColumnDef);
			}
		}

		processedColumns
			.filter((c) => c.formatter !== 'rowSelection')
			.forEach((col) => {
				// Auto header filter
				if (features.enableFiltering && !col.headerFilter) {
					if (col.type === 'number') col.headerFilter = 'number';
					else if (col.type === 'boolean') col.headerFilter = 'tickCross';
					else col.headerFilter = 'input';
				}

				if (col.type === 'boolean' && !col.formatter) {
					col.formatter = 'tickCross';
					col.hozAlign = 'center';
					col.formatterParams = { allowEmpty: true, allowTruth: true, allowFalse: false };
				}

				// Validation
				if (col.validation) {
					const validators: string[] = [];
					if (col.validation.required) validators.push('required');
					if (col.validation.min != null) validators.push(`min:${col.validation.min}`);
					if (col.validation.max != null) validators.push(`max:${col.validation.max}`);
					if (col.validation.minLength != null) validators.push(`minLength:${col.validation.minLength}`);
					if (col.validation.maxLength != null) validators.push(`maxLength:${col.validation.maxLength}`);
					if (validators.length) col.validator = validators as unknown as Validator[];
				}

				// Conditional formatting
				if (col.formatting?.length) {
					const baseFormatter = col.formatter;
					const rules = col.formatting;
					col.formatter = (cell: CellComponent, formatterParams: Record<string, unknown>, onRendered: (callback: () => void) => void) => {
						const value = cell.getValue() as unknown;
						const element: HTMLElement = cell.getElement();
						rules.forEach((rule) => {
							let match = false;
							if (rule.operator === '<' && value < rule.value) match = true;
							else if (rule.operator === '<=' && value <= rule.value) match = true;
							else if (rule.operator === '>' && value > rule.value) match = true;
							else if (rule.operator === '>=' && value >= rule.value) match = true;
							else if (rule.operator === '=' && value == rule.value) match = true;
							else if (rule.operator === '!=' && value != rule.value) match = true;
							if (match) {
								if (rule.color) element.style.color = rule.color;
								if (rule.bgColor) element.style.backgroundColor = rule.bgColor;
								if (rule.fontWeight) element.style.fontWeight = rule.fontWeight;
							}
						});
						if (typeof baseFormatter === 'function')
							return (
								baseFormatter as (
									cell: CellComponent,
									formatterParams: Record<string, unknown>,
									onRendered: (callback: () => void) => void
								) => string | HTMLElement
							)(cell, formatterParams, onRendered);
						return String(value ?? '');
					};
				}

				// Search highlight wrapper
				// ⚠️ Les formateurs natifs Tabulator (string: 'tickCross', 'rowSelection'...)
				// ne doivent PAS être wrappés — les écraser les casserait.
				const finalFormatter: Formatter | undefined = col.formatter;
				if (typeof finalFormatter !== 'function') return; // formateur natif → skip
				if ((finalFormatter as Formatter & { _searchWrapped?: boolean })._searchWrapped) return; // déjà wrappé → skip

				const wrappedFmt = (
					cell: CellComponent,
					formatterParams: Record<string, unknown>,
					onRendered: (callback: () => void) => void
				): string | HTMLElement => {
					const result = (
						finalFormatter as (
							cell: CellComponent,
							fp: Record<string, unknown>,
							or: (callback: () => void) => void
						) => string | HTMLElement
					)(cell, formatterParams, onRendered);
					if (result === null || result === undefined) return '';
					if (searchQuery && typeof result === 'string' && !result.includes('<')) {
						const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
						return result.replace(new RegExp(`(${escaped})`, 'gi'), `<span class="search-highlight">$1</span>`);
					}
					return result;
				};
				(wrappedFmt as unknown as { _searchWrapped: boolean })._searchWrapped = true;
				col.formatter = wrappedFmt;
			});

		return processedColumns;
	}
}
